import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      const role = res.user?.role;
      navigate(role === "direction" ? "/admin" : "/teacher");
    } else {
      setError(res.error || "Identifiants invalides");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col px-8 sm:px-16 py-10 bg-[var(--cream)]">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="back-home-link">
          <ArrowLeft size={16} /> Retour à l'accueil
        </Link>

        <div className="my-auto max-w-md w-full mx-auto">
          <div className="eyebrow mb-4">Espace professionnel</div>
          <h1 className="font-serif-display text-5xl font-semibold leading-tight">Bienvenue.</h1>
          <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">
            Connectez-vous avec vos identifiants pour accéder à votre tableau de bord.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="login-form">
            <div>
              <label className="eyebrow block mb-2">Identifiant</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="prenom.nom@lafayette.edu"
                className="w-full px-4 py-3 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] focus:ring-2 focus:ring-[var(--maroon)]/20 outline-none transition"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] focus:ring-2 focus:ring-[var(--maroon)]/20 outline-none transition"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 text-[var(--late)] text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="login-submit-btn">
              {submitting ? "Connexion..." : "Se connecter"}
            </button>
          </form>
        </div>

        <div className="text-xs text-[var(--muted)] mt-8">© 2026 Lafayette Progress</div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/1290141/pexels-photo-1290141.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
          alt="Bibliothèque élégante"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1010]/30 to-[#1a1010]/75" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="font-serif-display text-4xl italic">"L'excellence n'est jamais accidentelle."</div>
          <div className="mt-3 eyebrow !text-white/70">— Collège & Lycée Lafayette</div>
        </div>
      </div>
    </div>
  );
}
